import { useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateToString } from 'modules/core/lib/dates'

import { calculateWorkedTime } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Worked/lib/calculateWorkedTime'
import {
  formatDateRangeCopy,
  getDateRange,
} from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/lib/getDateRange'
import { useBalanceRange } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/lib/useBalanceRange'
import ClockInStore from 'modules/attendance/components/ClockIn/Store'
import { Layout } from 'modules/attendance/components/ClockIn/types'
import { useEmployeeSidepanelNavigation } from 'modules/attendance/hooks/useEmployeeSidepanelNavigation'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useContractsVersion } from 'modules/attendance/lib/contractsVersionContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import BalanceUtils from 'modules/attendance/lib/utils/Balance'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'

import { BalanceBreakdown } from './BalanceBreakdownWithTimeCategories'
import BankOfHoursCounters from './BankOfHoursCounters'
import { Compensation } from './Compensation'
import { WorkedVsPlanned } from './WorkedVsPlanned'

interface Props {
  showHourBalance: boolean
}

const HourlyCounterWithTimeCategories = ({ showHourBalance }: Props) => {
  const { month, year, employeeId: employeeIdFromParams, day } = useParams()
  const { employee } = useCurrentSession()
  const { startsOn, adjustedDailyMinutes } = useContractsVersion()

  const employeeId = employeeIdFromParams ? Number(employeeIdFromParams) : Number(employee?.id)
  const {
    employeesSettings,
    aggregatedWorkedTime: workedTime,
    estimatedTimes,
    balances,
    timeBlocksBalance,
    overtimePools,
    featureByName,
    loading,
    currentEmployeesSetting,
  } = useTimesheetData()

  const coercedProps = {
    year: Number(year),
    month: Number(month),
    employeeId: Number(employeeId),
    startsOn,
    currentEmployeeId: employee?.id,
    layout: Layout.employee,
    attendanceEmployeesSettingId: currentEmployeesSetting?.id,
  }
  const timeSheetDate = moment([Number(year), Number(month) - 1, Number(day)])
  // TODO: remove this store after having estimated times on graphql
  const store = new ClockInStore(coercedProps)

  const payrollCompensationEnabled =
    EmployeesSettingsUtils.hasPayrollCompensationEnabled(employeesSettings)

  const timeoffCompensationEnabled =
    EmployeesSettingsUtils.hasTimeoffCompensationEnabled(employeesSettings)

  const compensationEnabled = payrollCompensationEnabled || timeoffCompensationEnabled

  const { stringDateFrom: startOn, dateToOrToday } = useAttendanceDateNavigator()
  const hasBankOfHours = TimesheetFeatures.BANK_OF_HOURS in featureByName

  const endOn = dateToString(dateToOrToday, 'backend')

  const { endOnOrYesterday } = getDateRange(moment(endOn))
  const hideRange = startOn === timeBlocksBalance.startOn && endOn === endOnOrYesterday

  const determineRange = (startDate: string, endDate: string) => {
    // Hide the range in counters if their ranges match the date selected in the date picker.
    if (hideRange) return

    return formatDateRangeCopy(startDate, endDate)
  }

  const { balanceRangeStartOn, balanceRangeEndOn } = useBalanceRange(
    timeBlocksBalance.startOn,
    timeBlocksBalance.endOn
  )

  const balanceRange = determineRange(balanceRangeStartOn, balanceRangeEndOn)

  const workedTimeData = calculateWorkedTime({
    store,
    workedTime,
    estimatedTimes,
  })

  const contractHoursBaselineEnabled =
    EmployeesSettingsUtils.hasContractHoursBaseline(employeesSettings)

  const rangeIncludesToday = moment().isBetween(startOn, endOn, 'day', '[]')
  const dailyBalanceAggregate = BalanceUtils.sumDailyBalance(
    balances,
    startOn,
    endOn,
    !!adjustedDailyMinutes
  )

  const { handleTabClick } = useEmployeeSidepanelNavigation({
    tabFromNavigation: 'worked_hours',
    compensationEnabled,
    timeSheetDate,
    employeeId,
  })

  if (loading) return null

  return (
    <Box flexDirection="row" justifyContent="spaceBetween" width="full">
      {hasBankOfHours ? (
        <BankOfHoursCounters
          contractHoursBaselineEnabled={contractHoursBaselineEnabled}
          dailyBalanceAggregate={dailyBalanceAggregate}
          handleTabClick={handleTabClick}
          rangeIncludesToday={rangeIncludesToday}
          workedTimeData={workedTimeData}
          adjustedDailyMinutes={adjustedDailyMinutes}
          showHourBalance={showHourBalance}
        />
      ) : (
        <>
          <Box
            flexDirection="row"
            justifyContent="center"
            paddingY="s24"
            border={{
              all: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }}
            borderRadius={{ all: 'abs008' }}
          >
            <WorkedVsPlanned
              workedTimeData={workedTimeData}
              lineBorder={showHourBalance}
              range={determineRange(startOn, endOn)}
              employeeId={employeeId}
            />
            {showHourBalance && (
              <BalanceBreakdown
                overtimePools={overtimePools}
                timeBlocksBalance={timeBlocksBalance}
                employeeId={employeeId}
                range={balanceRange}
              />
            )}
          </Box>
          {compensationEnabled && (
            <Compensation
              employeeId={employeeId}
              timeBlocksBalance={timeBlocksBalance}
              range={balanceRange}
            />
          )}
        </>
      )}
    </Box>
  )
}

export default observer(HourlyCounterWithTimeCategories)
